import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fetchDevices } from '@/services/vmos-api';

type DeviceLike = {
  pad_id: string;
  device_name: string;
  platform: string | null;
  username: string | null;
};

function deviceLabel(d: DeviceLike): string {
  return [
    d.device_name,
    d.platform ? `· ${d.platform}` : '',
    d.username ? `(@${d.username})` : '',
  ].filter(Boolean).join(' ');
}

/**
 * Searchable multi-select of devices ("accounts"). Empty selection = any
 * account (global). Used for prompt/persona account scoping.
 */
export function AccountPicker({
  value,
  onChange,
  disabled = false,
  placeholder = 'Any account',
  className,
}: {
  value: string[];
  onChange: (padIds: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const { data } = useQuery({
    queryKey: ['vmos-devices'],
    queryFn: fetchDevices,
    staleTime: 60_000,
  });
  const devices = useMemo(() => data?.devices ?? [], [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter((d) =>
      deviceLabel(d).toLowerCase().includes(q) || d.pad_id.toLowerCase().includes(q),
    );
  }, [devices, query]);

  const toggle = (padId: string) => {
    onChange(value.includes(padId) ? value.filter((p) => p !== padId) : [...value, padId]);
  };

  const selected: DeviceLike[] = value.map((padId) => {
    const d = devices.find((x) => x.pad_id === padId);
    return d
      ? { pad_id: d.pad_id, device_name: d.device_name, platform: d.platform, username: d.username }
      : { pad_id: padId, device_name: padId, platform: null, username: null };
  });

  return (
    <div className={cn('space-y-1.5', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            disabled={disabled}
            className="w-full justify-between font-normal h-9 text-sm"
          >
            <span className={cn('truncate', value.length === 0 && 'text-muted-foreground')}>
              {value.length === 0
                ? placeholder
                : `${value.length} account${value.length === 1 ? '' : 's'} selected`}
            </span>
            <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-2" align="start">
          <Input
            placeholder="Search devices…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-8 text-xs mb-2"
          />
          <div className="max-h-60 overflow-y-auto space-y-0.5">
            {filtered.length === 0 ? (
              <div className="py-4 text-center text-xs text-muted-foreground">No devices match</div>
            ) : (
              filtered.map((d) => (
                <button
                  type="button"
                  key={d.pad_id}
                  onClick={() => toggle(d.pad_id)}
                  className="flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-muted/50 text-left"
                >
                  <Checkbox checked={value.includes(d.pad_id)} onCheckedChange={() => toggle(d.pad_id)} />
                  <span className="text-sm truncate flex-1">{d.device_name}</span>
                  {d.platform && <span className="text-[11px] text-muted-foreground shrink-0">{d.platform}</span>}
                  {d.username && <span className="text-[11px] text-muted-foreground font-mono shrink-0">@{d.username}</span>}
                </button>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((d) => (
            <span
              key={d.pad_id}
              className="inline-flex items-center gap-1 text-[11px] bg-muted rounded px-1.5 py-0.5"
            >
              {deviceLabel(d)}
              <button
                type="button"
                disabled={disabled}
                onClick={() => toggle(d.pad_id)}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
